import { useEffect } from 'react';
import { create } from 'zustand';
import { AdvanceQuestionType } from 'views/templates/personalAccountOpen/advanceQuestion/hooks';
import { EntryComplete } from 'views/templates/personalAccountOpen/entryComplete';
import { EntryConfirm } from 'views/templates/personalAccountOpen/entryConfirm';
import { CustomerInfoWrapper } from 'views/templates/personalAccountOpen/customerInfo';
import { usePersonalAccountEstablishStore } from 'views/templates/personalAccountOpen/hooks';
import { ErrorPage } from 'views/pages/errorPage';
import { PagePath } from 'constants/pagePath';
import { useGetAdvanceQuestionQuery } from 'views/utils/graphql/graphql';

export enum PersonalAccountOpenProfileDetailsPages {
  customerInfo,
  entryConfirm,
  entryComplete,
  errorPage,
}

export const usePersonalAccountOpenProfileDetails = () => {
  const { accountOpenProfileDetailsPageType, reset } = usePersonalAccountOpenProfileDetailsPageTypeStore();

  useEffect(() => {
    return () => {
      reset();
    };
  }, []);

  const SwitchPage = (page: PersonalAccountOpenProfileDetailsPages) => {
    switch (page) {
      // お客様情報
      case PersonalAccountOpenProfileDetailsPages.customerInfo:
        return <CustomerInfoWrapper />;

      // 申込確認
      case PersonalAccountOpenProfileDetailsPages.entryConfirm:
        return <EntryConfirm />;

      // 申込完了
      case PersonalAccountOpenProfileDetailsPages.entryComplete:
        return <EntryComplete />;

      case PersonalAccountOpenProfileDetailsPages.errorPage:
        return (
          <ErrorPage
            message={
              <>
                有効期限が切れているかURLに誤りがあります。
                再度、口座開設お申込の手続きを実施いただくようお願いいたします。
              </>
            }
            linkText="口座開設お手続きに進む"
            linkPath={PagePath.PersonalAccountOpen}
          />
        );
    }
  };

  return {
    accountOpenProfileDetailsPageType,
    SwitchPage,
  };
};

//画面遷移管理
type AccountOpenProfileDetailsPageTypeStore = {
  accountOpenProfileDetailsPageType: PersonalAccountOpenProfileDetailsPages;
  previousPage: PersonalAccountOpenProfileDetailsPages;
  setAccountOpenProfileDetailsPageType: (navType: PersonalAccountOpenProfileDetailsPages) => void;
  setPreviousPage: (navType: PersonalAccountOpenProfileDetailsPages) => void;
  reset: () => void;
};

export const usePersonalAccountOpenProfileDetailsPageTypeStore = create<AccountOpenProfileDetailsPageTypeStore>(
  (set) => ({
    accountOpenProfileDetailsPageType: PersonalAccountOpenProfileDetailsPages.customerInfo,
    previousPage: PersonalAccountOpenProfileDetailsPages.customerInfo,
    setAccountOpenProfileDetailsPageType: (navType: PersonalAccountOpenProfileDetailsPages) =>
      set((state) => ({ accountOpenProfileDetailsPageType: navType })),
    setPreviousPage: (navType: PersonalAccountOpenProfileDetailsPages) => set((state) => ({ previousPage: navType })),
    reset: () => set({ accountOpenProfileDetailsPageType: PersonalAccountOpenProfileDetailsPages.customerInfo }),
  }),
);

export const useAdvanceQuestionValueModule = (uniqueId: string | undefined) => {
  const { param, add, clear } = usePersonalAccountEstablishStore();
  const { setAccountOpenProfileDetailsPageType } = usePersonalAccountOpenProfileDetailsPageTypeStore();
  const [advanceQuestion, _] = useGetAdvanceQuestionQuery({
    variables: { id: uniqueId ?? '' },
    requestPolicy: 'network-only',
  });

  useEffect(() => {
    if (advanceQuestion.fetching) {
      return;
    } else if (
      advanceQuestion.data?.GetAdvanceQuestion == null ||
      advanceQuestion.data?.GetAdvanceQuestion?.RepresentAndDealerAreSame != null
    ) {
      setAccountOpenProfileDetailsPageType(PersonalAccountOpenProfileDetailsPages.errorPage);
    } else {
      clear();
      const info: AdvanceQuestionType = {
        uniqueId: uniqueId ?? '',
        nationality: advanceQuestion.data?.GetAdvanceQuestion?.AdvanceNationality ?? null,
        countryOfResidence: advanceQuestion.data?.GetAdvanceQuestion?.AdvanceCountryOfResidence ?? null,
        birthdate: new Date(advanceQuestion.data?.GetAdvanceQuestion?.Birthdate!) ?? null,
        mailAddressQuestion: advanceQuestion.data?.GetAdvanceQuestion?.DealerMailAddress,
        identificationQuestion: advanceQuestion.data?.GetAdvanceQuestion?.HaveEKYCDocument,
        selfieQuestion: advanceQuestion.data?.GetAdvanceQuestion?.CanTakePicture,
        personalInformation: true,
        generalTermsCondition: true,
        confirmResponsibility: true,
      };
      add({
        ...param,
        ...info,
      });

      setAccountOpenProfileDetailsPageType(PersonalAccountOpenProfileDetailsPages.customerInfo);
    }
  }, [advanceQuestion]);

  return {
    fetching: advanceQuestion.fetching,
  };
};
